import React, { useState } from 'react';
import { User, Task, Notification } from '../types';
import { useCustomDialog } from './CustomDialog';
import { createTask } from '../firebase';
import { PatientNameDisplay } from './PatientComponents';
import { DraggableWidgetGrid } from './dashboard/DraggableWidgetGrid';

interface DashboardStatsProps {
  user: User;
  dashboardStats: {
    totalCases: number;
    totalPatients: number;
    totalUsers: number;
    totalEvents: number;
    totalActivities: number;
    activeCases: number;
    archivedCases: number;
    inactiveCases: number;
    treatmentCompletionCount: number;
    recentlyUpdatedPatients: number;
    upcomingMeetings: number;
  };
  isStatsLoading: boolean;
  activityLogs: any[];
  totalActivityLogs: number;
  isActivityLoading: boolean;
  activitySearchTerm: string;
  setActivitySearchTerm: (term: string) => void;
  activityTimeFilter: 'all' | 'today' | 'lastWeek' | 'lastMonth';
  setActivityTimeFilter: (filter: 'all' | 'today' | 'lastWeek' | 'lastMonth') => void;
  activityCurrentPage: number;
  setActivityCurrentPage: (page: number) => void;
  activityTotalPages: number;
  activitiesPerPage: number;
  todayMilestones: any[];
  isTodayMilestonesLoading: boolean;
  handleDeleteMilestone: (milestoneId: string) => void;
  handlePatientSelect: (caseId: string) => void;
  handleDeleteActivityLog: (logId: string) => void;
  i18n: any;
  t: (key: string) => string;
  // New props for tasks and notifications
  todayTasks?: Task[];
  pendingTasks?: Task[];
  allTasks?: Task[];
  notifications?: Notification[];
  isTasksLoading?: boolean;
  isNotificationsLoading?: boolean;
  handleTaskStatusChange?: (taskId: string, status: 'pending' | 'inProgress' | 'completed') => void;
  handleMarkNotificationRead?: (notificationId: string) => void;
  handleDeleteTask?: (taskId: string) => void;
  handleDeleteNotification?: (notificationId: string) => void;
  refreshTasks?: () => void;
  // Events props
  events?: any[];
  isEventsLoading?: boolean;
  // Inactive patients
  inactivePatients?: any[];
  handleDeletePatient?: (caseId: string) => void;
  // Widget click handler
  onWidgetClick?: (widgetId: string, tab: string, filter?: any) => void;
}

export function DashboardStats({
  user,
  dashboardStats,
  isStatsLoading,
  activityLogs,
  totalActivityLogs,
  isActivityLoading,
  activitySearchTerm,
  setActivitySearchTerm,
  activityTimeFilter,
  setActivityTimeFilter,
  activityCurrentPage,
  setActivityCurrentPage,
  activityTotalPages,
  activitiesPerPage,
  todayMilestones,
  isTodayMilestonesLoading,
  handleDeleteMilestone,
  handlePatientSelect,
  handleDeleteActivityLog,
  i18n,
  t,
  // New props for tasks and notifications
  todayTasks = [],
  pendingTasks = [],
  allTasks = [],
  notifications = [],
  isTasksLoading = false,
  isNotificationsLoading = false,
  handleTaskStatusChange,
  handleMarkNotificationRead,
  handleDeleteTask,
  handleDeleteNotification,
  refreshTasks,
  // Events props
  events = [],
  isEventsLoading = false,
  // Inactive patients
  inactivePatients = [],
  handleDeletePatient,
  // Widget click handler
  onWidgetClick
}: DashboardStatsProps) {
  const { showConfirm, DialogComponent } = useCustomDialog();
  
  // Widget customization state
  const [activeWidgets, setActiveWidgets] = useState<string[]>(() => {
    const saved = localStorage.getItem('dashboardWidgets');
    const defaultWidgets = [
      'total-patients',
      'active-patients',
      'upcoming-events',
      'pending-tasks',
      'total-tasks',
      'completion-rate',
      'recent-activity',
      'ai-assistant'
    ];
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // If saved widgets exist but don't include all defaults, merge them
        // Ensure recent-activity is always included
        const merged = Array.from(new Set([...parsed, ...defaultWidgets]));
        // Make sure recent-activity is at the end
        const withoutActivity = merged.filter(w => w !== 'recent-activity');
        const withActivity = [...withoutActivity, 'recent-activity'];
        
        // If we added missing widgets, clear the layout so it regenerates with all widgets
        if (withActivity.length > parsed.length) {
          localStorage.removeItem('widgetLayout');
        }
        
        // Save merged widgets back to localStorage
        localStorage.setItem('dashboardWidgets', JSON.stringify(withActivity));
        return withActivity;
      } catch (e) {
        // If parsing fails, use defaults and save them, also clear layout
        localStorage.removeItem('widgetLayout');
        localStorage.setItem('dashboardWidgets', JSON.stringify(defaultWidgets));
        return defaultWidgets;
      }
    }
    // Default widgets - 6 small stats + 1 wide recent activity
    // Clear any old layout and save defaults to localStorage on first load
    localStorage.removeItem('widgetLayout');
    localStorage.setItem('dashboardWidgets', JSON.stringify(defaultWidgets));
    return defaultWidgets;
  });

  // Minimum required widgets: 6 small stats + 1 activity = 7 total
  const MINIMUM_STAT_WIDGETS = 6;
  const REQUIRED_ACTIVITY_WIDGET = 'recent-activity';
  const DEFAULT_STAT_WIDGETS = [
    'total-patients',
    'active-patients',
    'upcoming-events',
    'pending-tasks',
    'total-tasks',
    'completion-rate'
  ];

  const handleUpdateWidgets = (widgets: string[]) => {
    // Ensure activity widget is always included
    let finalWidgets = widgets.includes(REQUIRED_ACTIVITY_WIDGET) 
      ? widgets 
      : [...widgets, REQUIRED_ACTIVITY_WIDGET];
    
    // Filter out activity widget to count stat widgets
    const statWidgets = finalWidgets.filter(w => w !== REQUIRED_ACTIVITY_WIDGET);
    
    // If we have fewer than minimum stat widgets, add missing defaults
    if (statWidgets.length < MINIMUM_STAT_WIDGETS) {
      const missingStats = DEFAULT_STAT_WIDGETS.filter(w => !statWidgets.includes(w));
      const needed = MINIMUM_STAT_WIDGETS - statWidgets.length;
      finalWidgets = [
        ...statWidgets,
        ...missingStats.slice(0, needed),
        REQUIRED_ACTIVITY_WIDGET
      ];
    }
    
    // Ensure activity widget is at the end
    const withoutActivity = finalWidgets.filter(w => w !== REQUIRED_ACTIVITY_WIDGET);
    finalWidgets = [...withoutActivity, REQUIRED_ACTIVITY_WIDGET];
    
    // Clear layout to regenerate with new widgets
    localStorage.removeItem('widgetLayout');
    
    setActiveWidgets(finalWidgets);
    localStorage.setItem('dashboardWidgets', JSON.stringify(finalWidgets));
  };

  // Calculate widget data from existing stats
  // Calculate completion percentage: (archived cases / total cases) * 100
  const completionPercentage = dashboardStats.totalCases > 0
    ? Math.round((dashboardStats.archivedCases / dashboardStats.totalCases) * 100)
    : 0;

  const widgetData = {
    stats: {
      totalPatients: dashboardStats.totalPatients,
      activePatients: dashboardStats.activeCases,
      inactiveCases: dashboardStats.inactiveCases,
      treatmentCompletionCount: dashboardStats.treatmentCompletionCount,
      treatmentCompletionPercentage: completionPercentage,
      recentlyUpdated: dashboardStats.recentlyUpdatedPatients,
      totalCases: dashboardStats.totalCases,
      totalEvents: dashboardStats.totalEvents,
      upcomingEvents: dashboardStats.upcomingMeetings,
      totalTasks: allTasks.length,
      pendingTasks: pendingTasks.length,
      completionRate: Math.round(
        allTasks.length > 0 
          ? ((allTasks.filter(t => t.status === 'completed').length / allTasks.length) * 100)
          : 0
      ),
      newPatientsThisMonth: dashboardStats.recentlyUpdatedPatients
    },
    activityLogs,
    todayTasks,
    pendingTasks: pendingTasks,
    notifications,
    todayMilestones,
    inactivePatients,
    handlePatientSelect, // Pass the handler for navigation
    refreshTasks, // Pass refreshTasks for TodayTasksWidget
    handleDeleteTask, // Pass delete handler for tasks
    handleDeleteNotification, // Pass delete handler for notifications
    handleDeleteMilestone, // Pass delete handler for milestones
    handleDeleteActivityLog, // Pass delete handler for activity logs
    handleDeletePatient, // Pass delete handler for patients
    showConfirm, // Pass showConfirm for delete confirmations
    user // Pass user for permission checks
  };

  return (
    <div className="dashboard-stats">
      {/* Draggable Widget Grid */}
      <DraggableWidgetGrid
        activeWidgets={activeWidgets}
        onUpdateWidgets={handleUpdateWidgets}
        widgetData={widgetData}
        onWidgetClick={onWidgetClick}
      />

      <DialogComponent />
    </div>
  );
}
